/**
 * Google Maps URL Parser Utility
 * Extracts GPS coordinates from various Google Maps URL formats
 */

/**
 * Extract latitude and longitude from Google Maps URL
 * Supports multiple URL formats:
 * - https://www.google.com/maps/@12.97,77.64,15z
 * - https://www.google.com/maps/place/.../@12.97,77.64,15z
 * - https://www.google.com/maps?q=12.97,77.64
 *
 * @param {string} url - Google Maps URL
 * @returns {{ lat: number, lon: number } | null}
 */
export function extractCoordinates(url) {
  if (!url) return null;

  // Pattern 1: @lat,lon format (most common)
  // Handles: @12.9715987,77.5945627,15z
  const atPattern = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
  const atMatch = url.match(atPattern);
  if (atMatch) {
    return {
      lat: parseFloat(atMatch[1]),
      lon: parseFloat(atMatch[2])
    };
  }

  // Pattern 2: query parameter format
  // Handles: ?q=12.9715987,77.5945627
  const queryPattern = /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/;
  const queryMatch = url.match(queryPattern);
  if (queryMatch) {
    return {
      lat: parseFloat(queryMatch[1]),
      lon: parseFloat(queryMatch[2])
    };
  }

  // Pattern 3: center parameter (static maps)
  // Handles: center=12.9715987,77.5945627
  const centerPattern = /center=(-?\d+\.\d+),(-?\d+\.\d+)/;
  const centerMatch = url.match(centerPattern);
  if (centerMatch) {
    return {
      lat: parseFloat(centerMatch[1]),
      lon: parseFloat(centerMatch[2])
    };
  }

  return null;
}

/**
 * Validate extracted coordinates are within reasonable bounds
 * @param {{ lat: number, lon: number }} coords
 * @returns {boolean}
 */
export function validateCoordinates(coords) {
  if (!coords) return false;
  // Lat: -90 to 90, Lon: -180 to 180
  return (
    coords.lat >= -90 && coords.lat <= 90 &&
    coords.lon >= -180 && coords.lon <= 180
  );
}

/**
 * Format coordinates for display (6 decimal places = ~10cm accuracy)
 * @param {{ lat: number, lon: number }} coords
 * @returns {string}
 */
export function formatCoordinates(coords) {
  if (!coords) return 'Not set';
  return `${coords.lat.toFixed(6)}, ${coords.lon.toFixed(6)}`;
}

/**
 * Check if URL is a short Google Maps URL (goo.gl, maps.app)
 * @param {string} url
 * @returns {boolean}
 */
export function isShortUrl(url) {
  if (!url) return false;
  return url.includes('goo.gl') || url.includes('maps.app');
}
